<template>
  <section>
    <div class="col-4">
      <coozzy-form-input
        id="desiredStartOfContract"
        ref="desiredStartOfContract"
        v-model="desiredStartOfContract"
        :filter="'formatDate'"
        type="date"
        :name="$tc('message.applicationForm.desiredStartOfContract') + '*'"
        :tooltip-text="$tc('message.massRental.desiredStartOfContractTooltip')"
        :state="$v.desiredStartOfContract.$dirty && $v.desiredStartOfContract.$error ? false : null"
        :disabled="loading"
        @input="checkStartDate"
        @change="checkStartDate"
        @blur="checkStartDate" />
    </div>
    <div
      v-if="showTable"
      class="col-12 mt-3 position-relative">
      <div
        v-if="loading"
        class="objects-loading-overlay">
        <coozzy-spinner />
      </div>
      <div
        class="row"
        :class="{ 'objects-loading-dimmed': loading }">
        <h5 class="ml-3 pt-1 col-12">
          {{ listObjects.length }} {{ $t('message.onBoarding.buildings.objects.titleObjects' ) }}
        </h5>
        <div
          class="col-12">
          <div
            class="row mb-4">
            <div class="col-12 col-md mb-2 mb-md-0">
              <coozzy-form-input
                ref="searchText"
                v-model="searchText"
                :placeholder="$t('message.generic.search')"
                :disabled="loading"
                @keyup="searchInputText()" />
            </div>
            <div
              v-if="source === 'property'"
              class="col-12 col-md mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="searchBuilding"
                :options="buildingFilter"
                :multiple="true"
                :placeholder="$tc('message.onBoardingTabs.building')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabelBuilding"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="filterObjectList"
                @remove="filterObjectList" />
            </div>
            <div class="col-12 col-md mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="searchFloor"
                class="filter-inputs"
                :options="floorFilter"
                :multiple="true"
                :placeholder="$tc('message.tableColumnHeadings.floor')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabelFloor"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="filterObjectList"
                @remove="filterObjectList" />
            </div>
            <div class="col-12 col-md mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="searchRooms"
                class="filter-inputs"
                :options="roomsFilter"
                :multiple="true"
                :placeholder="$tc('message.tableColumnHeadings.rooms')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabel"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="filterObjectList"
                @remove="filterObjectList" />
            </div>
            <div class="col-12 col-md mb-2 mb-md-0">
              <coozzy-multiselect
                v-model="searchCategories"
                class="filter-inputs"
                :options="sortedFilterValues(categoryFilter, customLabelCategory, 'value')"
                :multiple="true"
                :placeholder="$tc('message.onBoarding.buildings.objects.category.title')"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="customLabelCategory"
                :track-by="'value'"
                :preselect-first="false"
                :taggable="false"
                @select="filterObjectList"
                @remove="filterObjectList" />
            </div>
          </div>
        </div>
      </div>
      <b-table
        id="table-groups"
        ref="table-groups"
        bordered
        responsive="true"
        stacked="md"
        class="mb-4"
        :class="{ 'objects-loading-dimmed': loading }"
        :fields="fields"
        :items="filteredObjects">
        <!-- Headings -->
        <template #head(checkbox)>
          <coozzy-form-checkbox
            ref="header_checkbox"
            :disabled="loading"
            @change="(value) => { handleCheckboxesInsideTable(value) }" />
        </template>
        <template #head(status)>
          {{ $t('message.tableColumnHeadings.status') }}
        </template>
        <template #head(objects)>
          {{ $t('message.tableColumnHeadings.objects') }}
        </template>
        <template #head(objectNumber)>
          {{ $t('message.tableColumnHeadings.objectNumber') }}
        </template>
        <template #head(numericId)>
          {{ $t('message.tableColumnHeadings.numericId') }}
        </template>
        <template #head(address)>
          {{ $t('message.tableColumnHeadings.address') }}
        </template>
        <template #head(type)>
          {{ $t('message.tableColumnHeadings.type') }}
        </template>
        <template #head(floor)>
          {{ $t('message.tableColumnHeadings.floor') }}
        </template>
        <template #head(rooms)>
          {{ $t('message.tableColumnHeadings.rooms') }}
        </template>
        <template #head(area)>
          {{ $t('message.tableColumnHeadings.area') }}
        </template>
        <template #head(tenant)>
          {{ $t('message.tableColumnHeadings.tenant') }}
        </template>
        <template #head(netRent)>
          {{ $t('message.tableColumnHeadings.netRent') }}
        </template>
        <template #head(grossRent)>
          {{ $t('message.tableColumnHeadings.grossRent') }}
        </template>
        <template #head(assignee)>
          {{ $t('message.tableColumnHeadings.assignee') }}
        </template>

        <!-- Body -->
        <template #cell(checkbox)="data">
          <coozzy-form-checkbox
            :id="'check_' + data.item.id"
            :initial="data.item.selected"
            :disabled="loading"
            @change="(value) => { itemSelected(data.item, value) }" />
        </template>
        <template #cell(status)="data">
          <div
            class="color-box mr-2 mt-2"
            :class="getFullStatusColorClass(data.item)" />
          <!-- eslint-disable vue/no-v-html -->
          <span v-html="getStatusText(data.item)" />
        <!-- eslint-enable vue/no-v-html -->
        </template>
        <template #cell(objects)="data">
          {{ data.item.name | displayOptionalValue }}
        </template>
        <template #cell(objectNumber)="data">
          {{ data.item.number | displayOptionalValue }}
        </template>
        <template #cell(numericId)="data">
          {{ data.item.numericId | displayOptionalValue }}
        </template>
        <template #cell(address)="data">
          <address-text :address="data.item.address" />
        </template>
        <template #cell(type)="data">
          <template v-if="data.item.category !== 'UNDEFINED_CATEGORY'">
            {{ $t('message.onBoarding.buildings.objects.category.' + data.item.category.toLowerCase()) }}
          </template>
          <template v-else>
            -
          </template>
        </template>
        <template #cell(floor)="data">
          <div class="text-center">
            {{ formatFloor(data.item.characteristics ? data.item.characteristics.floor : '') }}
          </div>
        </template>
        <template #cell(rooms)="data">
          <div class="text-center">
            {{ data.item.characteristics.rooms | displayOptionalValue }}
          </div>
        </template>
        <template #cell(area)="data">
          {{ getArea(data.item.characteristics.areas) | formatArea }}
        </template>
        <template #cell(tenant)="data">
          <div v-if="!data.item.condominiumOwnership && getSortedContactIdsCurrentTenancy(data.item.id).length">
            <router-link
              v-for="(contactId, tenantIndex) in getSortedContactIdsCurrentTenancy(data.item.id)"
              :key="contactId"
              :to="redirectToDetailPage(contactId)">
              {{ tenantIndex === 0 ? getContactName(contactId).substring(2) : getContactName(contactId) }}
            </router-link>
          </div>
          <span v-else>-</span>
        </template>
        <template #cell(netRent)="data">
          <div class="text-nowrap">
            <span>{{ getNetRent(data.item.id) | formatPrice }}</span>
          </div>
        </template>
        <template #cell(grossRent)="data">
          <span class="text-nowrap">{{ getTotalRent(data.item.id) | formatPrice }}</span>
        </template>
        <template #cell(assignee)="data">
          <span> {{ data.item.contacts && data.item.contacts.contactIdAssigneeAdministration !== '' ? getContactName(data.item.contacts.contactIdAssigneeAdministration).substring(2) : '-' }} </span>
        </template>
      </b-table>
    </div>
  </section>
</template>

<script lang="ts">

import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { buildValueCounts, sortFloorCounts } from '@/misc/utils/filterCounts'
import { routeChecks } from '@/mixins/routeChecks'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import { algolia } from '@/mixins/algolia'
import { mapActions, mapGetters } from 'vuex'
import AddressText from '@/framework/components/misc/AddressText.vue'
import Vue, { PropType } from 'vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'

import { countryIndex } from '@/mixins/countryIndex'
import { tables } from '@/mixins/tables'
import { required } from 'vuelidate/lib/validators'
// import types from api.ts
import {
  ObjectbuildingBuilding,
  Contactcontactv2Company, ObjectAreas,
  ObjectobjectObject,
  ObjecttenancyTenancy,
  V2Person
} from '@/types/api'
import CoozzyMultiselect from '@/framework/components/multiselect/CoozzyMultiselect.vue'
import debounce from 'debounce'
import { validation } from '@/mixins/validation'

type Contact = V2Person | Contactcontactv2Company
type Label = {
  count: number,
  value: string
}

interface SelectableObject extends ObjectobjectObject {
  selected: boolean;
}

export default Vue.extend({
  name: 'CreateMassRentalObjectsTable',
  components: {
    CoozzyMultiselect,
    CoozzyFormInput,
    AddressText,
    CoozzyFormCheckbox,
    CoozzySpinner
  },
  mixins: [formatting, obj, routeChecks, algolia, countryIndex, tables, validation],
  props: {
    source: {
      type: String,
      default: ''
    },
    objects: {
      type: Array as PropType<ObjectobjectObject[]>,
      default: () => []
    },
    tenancies: {
      type: Array as PropType<ObjecttenancyTenancy[]>,
      default: () => []
    },
    contacts: {
      type: Array as PropType<Contact[]>,
      default: () => []
    },
    buildings: {
      type: Array as PropType<ObjectbuildingBuilding[]>,
      default: () => []
    },
    requestObjectId: {
      type: String,
      default: ''
    },
    initialDesiredStartOfContract: {
      type: String,
      default: ''
    },
    selectedObjectIds: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedEntries: [],
      filteredObjects: [] as SelectableObject[],
      fields: [
        {
          key: 'checkbox',
          label: '',
          thClass: 'th-checkbox',
          tdClass: 'align-middle border-right-0 checkbox-cell'
        },
        {
          key: 'status',
          label: this.$t('message.tableColumnHeadings.status'),
          thClass: 'align-middle alignFoot',
          tdClass: 'align-middle'
        },
        {
          key: 'objects',
          label: this.$t('message.tableColumnHeadings.objects'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'objectNumber',
          label: this.$t('message.tableColumnHeadings.objectNumber'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'numericId',
          label: this.$t('message.tableColumnHeadings.numericId'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'address',
          label: this.$t('message.tableColumnHeadings.address'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'type',
          label: this.$t('message.tableColumnHeadings.type'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'floor',
          label: this.$t('message.tableColumnHeadings.floor'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center'
        },
        {
          key: 'rooms',
          label: this.$t('message.tableColumnHeadings.rooms'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center'
        },
        {
          key: 'area',
          label: this.$t('message.tableColumnHeadings.area'),
          thClass: 'align-middle text-center',
          tdClass: 'align-middle text-center'
        },
        {
          key: 'tenant',
          label: this.$t('message.tableColumnHeadings.tenant'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        },
        {
          key: 'netRent',
          label: this.$t('message.tableColumnHeadings.netRent'),
          thClass: 'align-middle text-right',
          tdClass: 'align-middle text-right'
        },
        {
          key: 'grossRent',
          label: this.$t('message.tableColumnHeadings.grossRent'),
          thClass: 'align-middle text-right',
          tdClass: 'align-middle text-right'
        },
        {
          key: 'assignee',
          label: this.$t('message.tableColumnHeadings.assignee'),
          thClass: 'align-middle',
          tdClass: 'align-middle'
        }
      ],
      // desiredStartOfContract: '',
      searchText: '',
      searchCategories: [],
      searchRooms: [],
      searchFloor: [],
      searchBuilding: []
    }
  },
  computed: {
    showTable() {
      const date = new Date(this.desiredStartOfContract)
      return this.isValidDate(date) && !this.disabledBefore1914(date)
    },
    floorFilter() {
      const counts = buildValueCounts<number>(this.listObjects.map((obj: ObjectobjectObject) => obj.characteristics.decimalFloor), [-10000])
      return counts.length > 0 ? sortFloorCounts(counts) : counts
    },
    roomsFilter() {
      return buildValueCounts<number>(this.listObjects.map((obj: ObjectobjectObject) => obj.characteristics.rooms), [-1])
    },
    categoryFilter() {
      return buildValueCounts<string>(this.listObjects.map((obj: ObjectobjectObject) => obj.category), ['UNDEFINED_CATEGORY'])
    },
    buildingFilter() {
      const counts = []
      const current = this
      current.listObjects.map((obj: ObjectobjectObject) => obj.buildingId).forEach(function(x: string) {
        const existingCount = counts.filter(e => e.value === x)
        if (existingCount.length === 0) {
          const sameNetwork = current.listObjects.map((obj: ObjectobjectObject) => obj.buildingId).filter((z: string) => z === x)
          if (sameNetwork.length > 1) {
            counts.push({
              count: sameNetwork.length,
              value: x
            })
          } else {
            counts.push({
              count: 1,
              value: x
            })
          }
        }
      })
      return counts
    },
    listObjects(): SelectableObject[] {
      const listObjects = this.isMarketingModule ? this.objects : this.objects.filter((obj: ObjectobjectObject) => this.getMarketingStatus(obj) === 'no')
      const newListObjects = listObjects.map((obj: ObjectobjectObject) => {
        return {
          ...obj,
          selected: this.isObjectSelected(obj.id)
        }
      })
      return this.sortObjectsByPriority(newListObjects)
    },
    selectedEntryIdSet(): Set<string> {
      return new Set(this.selectedEntries.map((entry: ObjectobjectObject) => entry.id))
    },
    tenanciesByObjectId(): Record<string, ObjecttenancyTenancy[]> {
      const map: Record<string, ObjecttenancyTenancy[]> = {}
      for (const ten of this.tenancies) {
        if (!ten?.objectId) {
          continue
        }
        if (!map[ten.objectId]) {
          map[ten.objectId] = []
        }
        map[ten.objectId].push(ten)
      }
      return map
    },
    contactsById(): Record<string, Contact> {
      const map: Record<string, Contact> = {}
      for (const contact of this.contacts) {
        if (contact?.id) {
          map[contact.id] = contact
        }
      }
      return map
    },
    desiredStartOfContract: {
      get() {
        return this.initialDesiredStartOfContract || ''
      },
      set(value: string) {
        this.selectedEntries = []
        this.$emit('selected-objects', [])
        const date = new Date(value)
        if (this.isValidDate(date)) {
          this.emitDesiredStartDateChanged(value)
        }
      }
    }
  },
  watch: {
    objects: function () {
      this.filterObjectList()
      this.rehydrateSelectionFromParent()
    },
    selectedObjectIds: function () {
      this.rehydrateSelectionFromParent()
    }
  },
  mounted() {
    this.rehydrateSelectionFromParent()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    isObjectSelected(objectId: string): boolean {
      if (!objectId) {
        return false
      }
      return this.selectedEntryIdSet.has(objectId)
    },
    emitSelectionChanged() {
      this.$emit('selected-objects', this.selectedEntries)
    },
    applySelectionBatch(items: ObjectobjectObject[], checked: boolean) {
      if (checked) {
        const byId = new Map(this.selectedEntries.map((entry: ObjectobjectObject) => [entry.id, entry]))
        items.forEach((item: ObjectobjectObject) => {
          if (item?.id) {
            byId.set(item.id, item)
          }
        })
        this.selectedEntries = Array.from(byId.values())
      } else {
        const removeIds = new Set(items.map((item: ObjectobjectObject) => item.id))
        this.selectedEntries = this.selectedEntries.filter((entry: ObjectobjectObject) => !removeIds.has(entry.id))
      }
      this.syncRowSelectedFlags()
      this.emitSelectionChanged()
    },
    syncRowSelectedFlags() {
      const selectedIds = new Set(this.selectedEntries.map((entry: ObjectobjectObject) => entry.id))
      this.filteredObjects.forEach((obj: SelectableObject) => {
        obj.selected = selectedIds.has(obj.id)
      })
    },
    rehydrateSelectionFromParent() {
      const ids = this.selectedObjectIds.filter((id: string) => id)
      if (this.objects.length === 0) {
        return
      }
      if (ids.length === 0) {
        if (this.selectedEntries.length === 0) {
          this.syncRowSelectedFlags()
        }
        return
      }
      if (this.selectedEntries.length > 0) {
        this.syncRowSelectedFlags()
        return
      }
      const idSet = new Set(ids)
      const items = this.objects.filter((obj: ObjectobjectObject) => idSet.has(obj.id))
      this.applySelectionBatch(items, true)
    },
    selectObject(additionalObjectsIds: string[] = []) {
      if (this.requestObjectId === '' && additionalObjectsIds.length === 0) {
        return
      }
      const extraIds = new Set(additionalObjectsIds)
      const items = this.objects.filter((obj: ObjectobjectObject) => obj.id === this.requestObjectId || extraIds.has(obj.id))
      if (items.length > 0) {
        this.applySelectionBatch(items, true)
      }
    },
    checkStartDate() {
      this.$v.$touch()
    },
    emitDesiredStartDateChanged: debounce(function (value: string) {
      this.$emit('desired-start-date-changed', value)
    }, 300),
    searchInputText: debounce(function () {
        this.filterObjectList()
    }, 300),
    customLabel(item: Label) {
      return item.value + ' (' + item.count + ')'
    },
    customLabelBuilding(item: Label) {
      return this.buildings.find((x: ObjectbuildingBuilding) => x.id === item.value).name + ' (' + item.count + ')'
    },
    customLabelFloor(item: Label) {
      return this.formatFloor(item.value) + ' (' + item.count + ')'
    },
    customLabelCategory(item: Label) {
      return this.$t('message.onBoarding.buildings.objects.category.' + item.value.toLowerCase()) + ' (' + item.count + ')'
    },
    filterObjectList() : void {
      const t = this
      this.$nextTick(function () {
        const searchLower = this.searchText.toLowerCase()
        const floorMapped = this.searchFloor.map((x: Label) => x.value)
        const roomMapped = this.searchRooms.map((x: Label) => x.value)
        const categoryMapped = this.searchCategories.map((x: Label) => x.value)
        const buildingMapped = this.searchBuilding.map((x: Label) => x.value)
        this.filteredObjects = this.listObjects.filter((item: ObjectobjectObject) => {
          const cdtFloor = floorMapped.includes(item.characteristics.decimalFloor) || this.searchFloor.length === 0
          const cdtRooms = roomMapped.includes(item.characteristics.rooms) || this.searchRooms.length === 0
          const cdtCategory = categoryMapped.includes(item.category) || this.searchCategories.length === 0
          const cdtBuilding = buildingMapped.includes(item.buildingId) || this.searchBuilding.length === 0

          const currentTenancy = this.getCurrentTenancy(item.id)
          let cdtSearch: boolean
          if (currentTenancy) {
            const currentTenancyTenant = currentTenancy.tenant.contactIds.map((element: string) => this.getContactName(element))
            const cdtSearchTenant = currentTenancyTenant.map((element: string) => element.toLowerCase().indexOf(searchLower) !== -1)
            cdtSearch = item.name.toLowerCase().includes(searchLower) || cdtSearchTenant.includes(true) || item.numericId.toString().includes(searchLower) || item.number.toString().includes(searchLower)
          } else {
            cdtSearch = item.name.toLowerCase().includes(searchLower) || item.numericId.toString().includes(searchLower) || item.number.toString().includes(searchLower)
          }
          return cdtFloor && cdtRooms && cdtCategory && cdtSearch && cdtBuilding
        }).sort(function(a: ObjectobjectObject, b: ObjectobjectObject) {
          if (a.inactiveBy !== null && new Date(t.$options.filters.objectToDateInput(a.inactiveBy)) <= new Date()) return 0
          if (b.inactiveBy === null) return 1
          return -1
        })
      })
    },
    getArea(area: ObjectAreas) {
      return area.effectiveArea !== -1 ? area.effectiveArea : area.livingArea ? area.livingArea : -1
    },
    clearAllSelectedObject() {
      this.selectedEntries = []
      this.handleCheckboxesInsideTable(false)
      if (this.$refs.header_checkbox) {
        this.$refs.header_checkbox.checked = false
      }
    },
    handleCheckboxesInsideTable(value: boolean) {
      this.applySelectionBatch(this.filteredObjects, value)
    },
    getSortedContactIdsCurrentTenancy(objectId: string) {
      // this function returns the contactIds of the current tenancy sorted by debtor first then tenants
      const startOfDayTimestamp = this.getStartOfDayTimestamp()
      const tenancy = this.getFilteredTenancies(objectId)
        .slice()
        .sort((a: ObjecttenancyTenancy, b: ObjecttenancyTenancy) => new Date(this.$options.filters.objectToDateInput(a.startingAt)).getTime() / 1000 > new Date(this.$options.filters.objectToDateInput(b.startingAt)).getTime() / 1000 ? -1 : 1)
        .find((t: ObjecttenancyTenancy) => t.startingAt && new Date(this.$options.filters.objectToDateInput(t.startingAt)).getTime() / 1000 <= startOfDayTimestamp && (!t.endingAt || (new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 < 0 || new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 >= startOfDayTimestamp)))
      let contactIds = []
      if (tenancy) {
        if (tenancy.tenant.contactIds.includes(tenancy.tenant.billingContactId)) {
          contactIds = [tenancy.tenant.billingContactId].concat(tenancy.tenant.contactIds.filter((x: string) => x !== tenancy.tenant.billingContactId))
        } else {
          contactIds = tenancy.tenant.contactIds
        }
      }
      return contactIds
    },
    redirectToDetailPage(contactId: string) {
      if (contactId !== null) {
        if (this.isOwnerModule) {
          return { name: 'OwnerContactDetailsView', params: { id: contactId } }
        } else if (this.isAssetModule) {
          return { name: 'AssetContactDetailsView', params: { id: contactId } }
        } else if (this.isAccountingModule) {
          return { name: 'AccountingContactDetailsView', params: { id: contactId } }
        } else {
          return { name: 'AdminContactDetailsView', params: { id: contactId } }
        }
      }
    },
    itemSelected(item: ObjectobjectObject, checked: boolean) {
      this.applySelectionBatch([item], checked)
    },
    getCurrentTenancy(objectId: string): ObjecttenancyTenancy {
      return this.getFilteredTenancies(objectId).find((t: ObjecttenancyTenancy) => t.status === 'TENANCY_STATUS_CURRENT' || t.status === 'TENANCY_STATUS_CURRENT_TERMINATED')
    },
    getContactName(contactId: string): string {
      const contact = this.contactsById[contactId]
      return contact ? ', ' + contact.name : '-'
    },
    getNetRent(objectId: string) {
      const tenancy = this.getCurrentTenancy(objectId)
      if (!tenancy) {
        return null
      }
      const adjustedRentValues = this.getAdjustedRentValues(tenancy)
      return adjustedRentValues.coldRent
    },
    getStartOfContract(objectId: string) {
      const tenancy = this.getCurrentTenancy(objectId)
      if (!tenancy) {
        return null
      }
      return this.$options.filters.objectToDate(tenancy.startingAt)
    },
    getTotalRent(objectId: string) {
      const tenancy = this.getCurrentTenancy(objectId)
      if (!tenancy) {
        return null
      }
      const adjustedRentValues = this.getAdjustedRentValues(tenancy)
      const total = (adjustedRentValues.heatingCosts !== -1 ? adjustedRentValues.heatingCosts : 0) +
        (adjustedRentValues.additionalCosts !== -1 ? adjustedRentValues.additionalCosts : 0) +
        (adjustedRentValues.coldRent !== -1 ? adjustedRentValues.coldRent : 0)
      return total !== 0 ? total : null
    },
    getAdjustedRentValues(tenancy: ObjecttenancyTenancy) {
      let selectedTenancy = tenancy.rent.adjustments[0]
      const current = this
      const pastAdjustements = tenancy.rent.adjustments.filter(obj => obj.byDate && new Date(current.$options.filters.objectToDateInput(obj.byDate)).getTime() < Date.now())
      if (pastAdjustements.length > 0) {
        pastAdjustements.sort(function (a, b) {
          return new Date(current.$options.filters.objectToDateInput(b.byDate)).getTime() - new Date(current.$options.filters.objectToDateInput(a.byDate)).getTime()
        })
        selectedTenancy = pastAdjustements[0]
      }
      return selectedTenancy
    },
    getStatusText(obj: ObjectobjectObject) {
      if (!obj) {
        return 'n/a'
      }
      return this.getFullStatusText(obj)
    },
    getFilteredTenancies(objectId: string): ObjecttenancyTenancy[] {
      return this.tenanciesByObjectId[objectId] || []
    },
    getFullStatusColorClass(obj: ObjectobjectObject) {
      return this.getStatus(obj)
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToError()
      }
      return this.$v.$invalid
    }
  },
  validations() {
    return {
      desiredStartOfContract: {
        required
      }
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(.custom-checkbox .custom-control-input) {
  pointer-events: none;
}
.objects-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  min-height: 120px;
}
.objects-loading-dimmed {
  pointer-events: none;
  opacity: 0.55;
}
</style>
